import { notifyOnError } from '~/rage/decorators';
import { IObservable, Subject } from '~/rage/utils';
import {
	ClientError,
	ClientErrorCode,
	INumberPlatePrice,
	VehicleNumberPlate,
	VehicleNumberPlateType,
	WalletType,
	calculateNumberPlatePrice,
} from '~/shared';
import { BackendService } from '../BackendService';
import { GameConfigService } from '../GameConfigService';
import { INotificationsService } from '../NotificationsService';
import { IRpcService } from '../RpcService';
import { VarsService } from '../VarsService';
import { IVehicleService } from '../VehicleService';
import { IStorageNumberPlate, IVehicleNumberPlateService } from './types';

export class VehicleNumberPlateService implements IVehicleNumberPlateService {
	private storedNumberPlates = new Subject<IStorageNumberPlate[]>([]);
	private regionNameByCode = new Map<string, string>();

	constructor(
		private readonly backend: BackendService,
		private readonly vehicles: IVehicleService,
		private readonly rpc: IRpcService,
		private readonly notifications: INotificationsService,
		private readonly gameConfig: GameConfigService,
		private readonly vars: VarsService,
	) {
		this.gameConfig.getConfigs().subscribe((config) => {
			if (!config || !config.vehicleNumberPlates) {
				return;
			}

			this.regionNameByCode.clear();
			for (const region of config.vehicleNumberPlates.regions) {
				this.regionNameByCode.set(region.code, region.name);
			}
		});

		this.backend.addEventHandler(
			'vehicleNumberPlates:storageUpdated',
			(storedNumberPlates: IStorageNumberPlate[]) => {
				this.storedNumberPlates.next(storedNumberPlates);
			},
		);
	}

	public getStoreNumberPlatePrice(): number {
		return this.gameConfig.configs?.vehicleNumberPlates?.storagePrice ?? 0;
	}

	public getStoreNumberPlateMaxDays(): number {
		return this.gameConfig.configs?.vehicleNumberPlates?.storageMaxDays ?? 0;
	}

	@notifyOnError
	public async previewNumberPlate(
		numberPlate: VehicleNumberPlate,
	): Promise<void> {
		await this.rpc.invokeServer('vehicleNumberPlates:setPreview', numberPlate);
	}

	@notifyOnError
	public async resetPreviewNumberPlate(): Promise<void> {
		await this.rpc.invokeServer('vehicleNumberPlates:resetPreview');
	}

	@notifyOnError
	public async buyNumberPlate(
		numberPlate: VehicleNumberPlate,
		wallet: WalletType,
	): Promise<void> {
		await this.backend.invoke('vehicleNumberPlates:buy', {
			vehicleId: this.vehicles.getLocalOwnedVehicleId().getValue(),
			numberPlate,
			wallet,
		});

		const isCustom =
			numberPlate.type === VehicleNumberPlateType.Custom ||
			numberPlate.type === VehicleNumberPlateType.CustomBlack;

		if (isCustom) {
			this.notifications.showInfo(
				'Заявка на именной номерной знак отправлена на модерацию',
			);
		} else {
			this.notifications.showSuccess('Вы успешно приобрели номерной знак!');
		}

		await this.resetPreviewNumberPlate();
	}

	@notifyOnError
	public async getNumberPlatePrice(
		numberPlate: VehicleNumberPlate,
		wallet: WalletType,
	): Promise<INumberPlatePrice | null> {
		const numberPlateConfig = this.gameConfig.configs!.vehicleNumberPlates;
		if (!numberPlateConfig) {
			return null;
		}
		return calculateNumberPlatePrice(
			numberPlate,
			numberPlateConfig,
			wallet === WalletType.Donate,
		);
	}

	@notifyOnError
	public async moveNumberPlateToStorage(): Promise<void> {
		if (
			this.storedNumberPlates.getValue().length >=
			(this.gameConfig.configs?.vehicleNumberPlates?.storageMaxItems ?? 0)
		) {
			throw new ClientError(ClientErrorCode.VehicleNumberPlateStorageFull);
		}

		await this.backend.invoke('vehicleNumberPlates:moveToStorage', {
			vehicleId: this.vehicles.getLocalOwnedVehicleId().getValue(),
		});

		this.notifications.showSuccess(`Номер перемещен в хранилище!`);
	}

	@notifyOnError
	public async moveNumberPlateFromStorage(
		numberPlate: VehicleNumberPlate,
		deleteCurrent: boolean,
	): Promise<void> {
		await this.backend.invoke('vehicleNumberPlates:moveFromStorage', {
			vehicleId: this.vehicles.getLocalOwnedVehicleId().getValue(),
			numberPlate,
			deleteCurrent,
		});

		let plateName = `${numberPlate?.text ?? ''}${numberPlate?.region ?? ''}`;
		if (plateName.length === 0) {
			plateName = '???';
		}

		if (deleteCurrent) {
			this.notifications.showSuccess(
				`Номер был заменен на ${plateName} из хранилища`,
			);
		} else {
			this.notifications.showSuccess(
				`Номер ${plateName} был установлен из хранилища`,
			);
		}
	}

	@notifyOnError
	public async cancelNamedNumberPlateChange(): Promise<void> {
		await this.backend.invoke('vehicleNumberPlates:cancelNamedChange');
	}

	public getStoredNumberPlates(): IObservable<IStorageNumberPlate[]> {
		return this.storedNumberPlates;
	}

	public getRegionNameByCode(code: string): string | null {
		return this.regionNameByCode.get(code) || null;
	}

	public getLocalOwnedVehicleNumberPlate(): IObservable<VehicleNumberPlate | null> {
		return this.vars.get('localOwnedVehicleNumberPlate', null);
	}

	public async exitMenu(): Promise<void> {
		await this.resetPreviewNumberPlate();
		await this.rpc.invokeClient('vehicleNumberPlates:exitMenu');
	}
}
